import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MapPin, ArrowRight, TrainFront, HeartPulse, GraduationCap, Briefcase, ShieldCheck, Palmtree } from 'lucide-react';
import Seo, { orgSchema, websiteSchema, listingSchema, faqSchema } from '@/components/Seo';
import Marquee from '@/components/Marquee';
import SectionHeading from '@/components/SectionHeading';
import Reveal from '@/components/Reveal';
import CTAPanel from '@/components/CTAPanel';
import { EnquireButton, SiteVisitButton, WhatsAppButton, CallButton } from '@/components/CTAButtons';
import { HIGHLIGHTS, CONFIGS, AMENITIES, CONNECTIVITY, FAQS, RERA_LIST, IMAGES, GALLERY } from '@/data/siteData';

const MaskedLine = ({ children, delay = 0, as: Tag = 'span' }) => (
  <span className="hero-mask-line">
    <motion.span
      className="block"
      initial={{ y: '110%' }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Tag>{children}</Tag>
    </motion.span>
  </span>
);

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <>
      <Seo
        title="Provident Palm Vista Shilphata | 1, 2 & 2.5 BHK"
        description="Provident Palm Vista Shilphata — 1, 2 & 2.5 BHK homes with balconies on Kalyan-Shilphata Road, Mumbai. 16 acres, 70% open spaces, 60+ amenities. Get price & floor plans."
        path="/"
        jsonLd={[orgSchema, websiteSchema, listingSchema, faqSchema(FAQS.slice(0, 6))]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden bg-palm" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={IMAGES.hero} alt="Provident Palm Vista Shilphata tower elevation render" className="h-[120%] w-full object-cover" fetchpriority="high" width={1039} height={1039} />
          <div className="absolute inset-0 bg-palm/45" />
          <div className="absolute inset-0 bg-gradient-to-t from-palm via-palm/70 to-palm/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-palm/70 via-palm/25 to-transparent" />
        </motion.div>
        <motion.div style={{ opacity: fade }} className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 md:pb-20 pt-32">
          <MaskedLine delay={0.15}>
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-palm/40 backdrop-blur px-4 py-1.5 text-[11px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-gold">
              <MapPin className="h-3.5 w-3.5" /> Kalyan–Shilphata Road, Mumbai
            </span>
          </MaskedLine>
          <h1 className="font-display font-medium text-ivory leading-[1.02] mt-6 text-4xl sm:text-5xl lg:text-7xl tracking-tight" data-testid="hero-h1">
            <MaskedLine delay={0.3}>Provident Palm Vista Shilphata</MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="text-gold-grad italic">1, 2 & 2.5 BHK Homes</span>
            </MaskedLine>
            <MaskedLine delay={0.6}>
              <span className="text-ivory/85 text-2xl sm:text-3xl lg:text-5xl">on Kalyan-Shilphata Road</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-6 max-w-xl text-sm sm:text-base text-ivory/75 leading-relaxed"
          >
            A 16-acre themed tropical ecosystem with 70% open spaces, 60+ world-class amenities and homes designed with balconies — 5 minutes from Shilphata Junction.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
            className="flex flex-wrap items-center gap-3 mt-8"
          >
            <EnquireButton testid="hero-get-price-btn" />
            <SiteVisitButton testid="hero-site-visit-btn" />
            <WhatsAppButton label="WhatsApp" testid="hero-whatsapp-btn" className="!bg-transparent border border-[#4be07f]/50 !text-[#4be07f] hover:!bg-[#1FAF54]/15 shadow-none" />
            <CallButton label={'+91 88798 56585'} testid="hero-call-btn" dark />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden mt-12 max-w-3xl border border-white/10 backdrop-blur"
            data-testid="hero-stats"
          >
            {[['16', 'Acres'], ['60+', 'Amenities'], ['70%', 'Open Spaces'], ['100%', 'Homes with Balconies']].map(([n, l]) => (
              <div key={l} className="bg-palm/55 px-5 py-4">
                <p className="font-display text-2xl sm:text-3xl text-gold font-medium">{n}</p>
                <p className="text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-ivory/60 mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* 01 HIGHLIGHTS */}
      <section className="py-16 md:py-24" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading number="01" eyebrow="Project Highlights" title="A tropical ecosystem, measured in superlatives" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.label} delay={i * 0.07}>
                <div className="group h-full bg-white border border-line rounded-2xl p-7 hover:shadow-xl hover:border-gold/40 transition-all duration-300">
                  <p className="font-display text-5xl font-medium text-palm group-hover:text-gold transition-colors">
                    {h.num}<span className="text-2xl text-gold">{h.unit}</span>
                  </p>
                  <h3 className="font-semibold text-palm mt-3">{h.label}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{h.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 02 ABOUT */}
      <section className="py-16 md:py-24 bg-paper grain relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <SectionHeading number="02" eyebrow="About the Project" title="About Provident Palm Vista Shilphata" />
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <Reveal className="lg:col-span-7 space-y-5 text-[15px] leading-relaxed text-ink/80">
              <p>
                Provident Palm Vista is a 16-acre themed tropical residential ecosystem on Kalyan–Shilphata Road, Mumbai, developed by Provident Housing — a Puravankara company. Conceived around the idea of a “future perfect life”, the project brings together spacious 1, 2 and 2.5 BHK homes with balconies, nearly 70% open spaces and a landscape built around 30 varieties of palm trees.
              </p>
              <p>
                What sets Provident Palm Vista Shilphata apart is the deliberate focus on openness. Rather than packing the land parcel with towers, the master plan gives most of the ground back to residents — as layered plantations, sensory trails, a secret garden, water edges, decks and lawns. The result is a community where the space between buildings matters as much as the homes themselves.
              </p>
              <p>
                The amenity programme is unusually deep for this segment: 60+ world-class amenities organised into zones for kids (play area, climbing wall, cricket nets, tricycle track, kids’ pool), for grown-ups (swimming pool, jogging and walking tracks, multi-play court, cafe deck, Wi-Fi lounge, putting lawn, reflexology pathway) and for elders (seniors’ deck, spill-over lawn, reading and working coves, ledge and cove seating). Every age group gets a landscape of its own.
              </p>
              <p>
                Location is the project’s second pillar. Sitting next to the Indian Oil Pump on Kalyan–Shilphata Road, about 5 minutes from Shilphata Junction, Palm Vista connects comfortably to the three employment anchors of the region — Thane, Navi Mumbai and the Kalyan–Dombivli belt. Diva Station is roughly 3 minutes away, Thane–Belapur Road about 15 minutes, the Eastern Express Highway about 20 minutes, and business districts like Millennium Business Park, Reliance Corporate Park and Mindspace Airoli within a 12–20 minute band. Reputed schools and multi-speciality hospitals sit within minutes of the site.
              </p>
              <p>
                Shilphata itself has emerged as one of the Mumbai Metropolitan Region’s most closely watched growth corridors. The node sits on planned metro routes and the wider airport-linked infrastructure story, which the original project material highlighted as long-term drivers. For buyers, the practical takeaway is simpler: a branded, RERA-registered community with genuine open space, at ticket sizes that remain well below Thane and central Navi Mumbai.
              </p>
              <p>
                Homes at Provident Palm Vista are available in three configurations — compact 1 BHK residences in two sizes, family-friendly 2 BHK homes, and expansive 2.5 BHK layouts with a flexible extra room. Every home includes a balcony, extending daily life into the tropical landscape. To explore current availability, pricing and floor plans, use the enquiry options on this page or book a site visit.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <EnquireButton testid="about-enquire-btn" />
                <Link to="/overview" data-testid="about-overview-link" className="inline-flex items-center gap-2 rounded-full border border-palm/25 text-palm px-6 py-3 text-sm font-semibold hover:border-gold hover:text-gold transition-all">
                  Full Project Overview <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="relative">
                <img src={IMAGES.palmArch} alt="Tropical palm landscaping at Provident Palm Vista Shilphata" className="rounded-3xl w-full object-cover aspect-[4/5] shadow-2xl" loading="lazy" width={1200} height={1800} />
                <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-palm text-ivory rounded-2xl p-5 shadow-xl max-w-[240px]">
                  <Palmtree className="h-6 w-6 text-gold mb-2" strokeWidth={1.5} />
                  <p className="font-display text-lg leading-snug">“Come home to a future perfect life.”</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 03 HOMES */}
      <section className="py-16 md:py-24" data-testid="homes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading number="03" eyebrow="Residences" title="1, 2 & 2.5 BHK homes with balconies" sub="Three configurations, one tropical address. Every home at Provident Palm Vista is planned with a balcony and full access to the 16-acre amenity ecosystem." />
          <div className="grid md:grid-cols-3 gap-6">
            {CONFIGS.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.1}>
                <div className="group h-full flex flex-col bg-white border border-line rounded-3xl overflow-hidden hover:shadow-2xl hover:border-gold/40 transition-all duration-300">
                  <div className="relative h-52 overflow-hidden">
                    <img src={[IMAGES.balcony, IMAGES.pool, IMAGES.clubhouse][i]} alt={`Provident Palm Vista ${c.name} homes`} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" width={1200} height={800} />
                    <span className="absolute top-4 left-4 rounded-full bg-palm/85 backdrop-blur text-gold text-[11px] font-bold tracking-[0.2em] uppercase px-4 py-1.5">{c.name}</span>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-baseline justify-between">
                      <p className="font-display text-3xl text-palm font-medium">{c.price}</p>
                      <p className="text-xs text-muted-foreground">{c.carpet} carpet</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{c.priceNote}</p>
                    <p className="text-sm text-muted-foreground mt-4 leading-relaxed flex-1">{c.blurb}</p>
                    <p className="text-xs text-sage font-semibold mt-3">Best for: {c.who}</p>
                    <div className="flex flex-wrap gap-2 mt-5">
                      <EnquireButton label="Get Price & Floor Plan" testid={`home-${c.slug}-price-btn`} className="!px-4 !py-2.5 !text-xs" />
                      <Link to={`/${c.slug}`} data-testid={`home-${c.slug}-link`} className="inline-flex items-center gap-1.5 rounded-full border border-palm/25 text-palm px-4 py-2.5 text-xs font-semibold hover:border-gold hover:text-gold transition-all">
                        Explore {c.name} <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 04 TROPICAL ECOSYSTEM */}
      <section className="relative py-16 md:py-28 bg-palm overflow-hidden" data-testid="ecosystem-section">
        <div className="absolute inset-0 opacity-[0.18]">
          <img src={IMAGES.clubhouse} alt="" aria-hidden="true" className="h-full w-full object-cover" loading="lazy" width={1486} height={836} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading dark number="04" eyebrow="The Tropical Ecosystem" title="30 varieties of palms. One living, breathing landscape." />
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { t: 'Layered Plantation', d: 'Plantation islands and layered planting create depth, shade and privacy across the 16-acre expanse.' },
              { t: 'Sensory Trail & Secret Garden', d: 'Curated walks designed for pause — a sensory trail and a tucked-away secret garden within the community.' },
              { t: 'Water Edges & Decks', d: 'Water edges, lounge decks and gazebo sit-outs turn the landscape into everyday living space.' },
            ].map((f, i) => (
              <Reveal key={f.t} delay={i * 0.1}>
                <div className="rounded-3xl border border-ivory/15 bg-ivory/5 backdrop-blur p-8 h-full hover:border-gold/40 transition-colors">
                  <span className="font-display text-4xl text-gold/60 font-medium">0{i + 1}</span>
                  <h3 className="font-display text-2xl text-ivory font-medium mt-3">{f.t}</h3>
                  <p className="text-sm text-ivory/65 mt-3 leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 05 AMENITIES PREVIEW */}
      <section className="py-16 md:py-24" data-testid="amenities-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading number="05" eyebrow="60+ Amenities" title="An amenity zone for every generation" sub="Kids, fitness, seniors, community and landscape — five zones, more than sixty experiences." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {AMENITIES.map((g, i) => (
              <Reveal key={g.cat} delay={i * 0.06}>
                <Link to="/provident-palm-vista-amenities" data-testid={`amenity-cat-${g.cat.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className="block h-full bg-white border border-line rounded-2xl p-6 hover:shadow-xl hover:border-gold/40 transition-all group">
                  <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold">{g.cat}</p>
                  <p className="font-display text-2xl text-palm mt-2">{g.items.length}+ <span className="text-sm text-muted-foreground font-sans">experiences</span></p>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{g.items.slice(0, 3).join(' · ')}…</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-palm mt-4 group-hover:text-gold transition-colors">View all <ArrowRight className="h-3 w-3" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 06 LOCATION */}
      <section className="py-16 md:py-24 bg-paper grain relative" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <SectionHeading number="06" eyebrow="Location & Connectivity" title="Right on Kalyan–Shilphata Road" sub="Shilphata sits at the junction of the corridors towards Thane, Panvel and the Navi Mumbai business districts. Distances below are as per official project material." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CONNECTIVITY.map((g, i) => {
              const icons = [TrainFront, HeartPulse, Briefcase, GraduationCap];
              const Icon = icons[i];
              return (
                <Reveal key={g.cat} delay={i * 0.07}>
                  <div className="h-full bg-white border border-line rounded-2xl p-6">
                    <div className="flex items-center gap-2.5 mb-4">
                      <span className="h-9 w-9 rounded-full bg-paper flex items-center justify-center"><Icon className="h-4 w-4 text-gold" /></span>
                      <h3 className="font-semibold text-palm text-sm">{g.cat}</h3>
                    </div>
                    <ul className="space-y-2.5">
                      {g.items.map((it) => (
                        <li key={it.name} className="flex items-center justify-between gap-2 text-[13px]">
                          <span className="text-ink/75">{it.name}</span>
                          <span className="text-gold font-semibold whitespace-nowrap">{it.time}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-8">
            <Link to="/provident-palm-vista-location" data-testid="location-detail-link" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">
              Explore the full Provident Palm Vista location story <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="py-16 md:py-24" data-testid="gallery-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading number="07" eyebrow="Gallery" title="A glimpse of the palm life" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {GALLERY.slice(0, 4).map((g, i) => (
              <Reveal key={g.src} delay={i * 0.06} className={i === 0 ? 'col-span-2 row-span-2' : ''}>
                <img src={g.src} alt={g.alt} loading="lazy" width={g.w} height={g.h}
                  className="rounded-2xl w-full object-cover h-full min-h-[180px] hover:opacity-95 transition-opacity" />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-6">
            <Link to="/gallery" data-testid="gallery-link" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">
              View the full gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* RERA */}
      <section className="py-14 md:py-20 bg-ivory border-y border-line" data-testid="rera-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center gap-8">
            <Reveal className="lg:w-1/3">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-8 w-8 text-gold" strokeWidth={1.5} />
                <h2 className="font-display text-2xl sm:text-3xl font-medium text-palm">RERA Registered</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Provident Palm Vista is registered under MahaRERA, wing-wise. Verify any registration at maharera.mahaonline.gov.in.
              </p>
            </Reveal>
            <Reveal delay={0.1} className="lg:w-2/3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {RERA_LIST.map((r) => (
                  <div key={r.rera} className="bg-white border border-line rounded-xl px-4 py-3 text-center">
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-gold">{r.wing}</p>
                    <p className="text-xs font-mono text-palm mt-1">{r.rera}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="py-16 md:py-24" data-testid="faq-preview">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading align="center" eyebrow="FAQs" title="Questions buyers ask us" />
          <div className="space-y-3">
            {FAQS.slice(0, 5).map((f) => (
              <Reveal key={f.q}>
                <details className="group bg-white border border-line rounded-2xl px-6 py-4 open:shadow-md transition-shadow">
                  <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-palm text-sm sm:text-base">
                    {f.q}
                    <span className="text-gold text-xl font-light group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-8">
            <Link to="/faqs" data-testid="faqs-link" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">
              View all frequently asked questions <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTAPanel />
    </>
  );
};

export default Home;
